//libraries imports
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

//Core Components
import Loader from "../../../core/common/loader";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import DeleteModal from "../../../core/modals/deleteModal";
import CommonSelect from "../../../core/common/commonSelect";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import Table from "../../../core/common/dataTable";

// 🛠️ Helpers
import api from "../../../services/api";
import { notify } from "../../../services/toast.config";
import { exportToExcel, exportToPDF } from "../../../utils/exportData";
import { truncateCompanyText } from "../../../helper/company_helper";
import { humanReadableDate } from "../../../helper/dateTimeHelper.js";
import AddProductModal from "./components/addProduct";
import EditProductModal from "./components/editProduct";
import { all_routes } from "../../router/all_routes";
import ProductFilter from "./components/productFilter";
import AssetProductGrid from "./asset-product-grid";
import { toProperCase } from "../../../helper/textHelper";
import ProductGridFilter from "./components/productGridFilter";
import MarkProductLostModal from "./components/markProductLostModal";

//Constants
const EMPTY_COMPANY = {
  company_name: "Select Company",
  company_id: "",
  company_type: "Type",
};

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const AssetsProduct = () => {
  // 🔹 Local Storage
  const signature = localStorage.getItem("signature");

  //grid - list toggling
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get("view") || "list";
  const isGridView = viewMode === "grid";

  const handleViewChange = (newView: any) => {
    const params = new URLSearchParams(searchParams);
    params.set("view", newView);
    setSearchParams(params);
  };

  // 🔹 State: UI
  const [isLoading, setIsLoading] = useState(false);

  // 🔹 State: Data
  const [formDataCompanyChoose, setFormDataCompanyChoose] =
    useState(EMPTY_COMPANY);
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [assetCategoryOptions, setAssetCategoryOptions] = useState<any[]>([]);
  const [selectedProductEdit, setSelectedProductEdit] = useState<any | null>(
    null,
  );
  const [selectedLostProduct, setSelectedLostProduct] = useState<any | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState<string>("");

  // 🔹 State: Modal Actions
  const [deleteRecord, setDeleteRecord] = useState<{
    id: number;
    Location: string;
  } | null>(null);

  // Derived Data
  const companyOptions = companies.map((company) => ({
    value: company.id.toString(),
    label: `${company.name} - ${company.type}`,
    companyName: company.name,
  }));
  const statusOptions = [...STATUS_OPTIONS];

  // 🧩 Handlers: Form Inputs
  const handleCompanySelection = (selectedOption: any) => {
    if (!selectedOption) return;

    const newFormData = {
      company_id: selectedOption.value,
      company_name: selectedOption.label.split("-")[0]?.trim() || "",
      company_type: selectedOption.label.split("-")[1]?.trim() || "",
    };

    localStorage.setItem("selectedAssetCompany", JSON.stringify(newFormData));

    setFormDataCompanyChoose((prev: any) => ({
      ...prev,
      ...newFormData,
    }));
  };

  const handleProductEdit = (record: any) => {
    try {
      setSelectedProductEdit(record);
    } catch (e: any) {
      notify.error(e?.message || "Error occured while editing!");
    }
  };

  // 🗑️ Delete: Product
  const handleDelete = async () => {
    if (deleteRecord) {
      try {
        var productId = deleteRecord.id;
        try {
          setIsLoading(true);
          const response = await api.post("userService/deleteProduct", {
            signature,
            id: productId,
          });
          notify.success(response.data.message[0].msg);
        } catch (error: any) {
          console.log("Error", error);
          notify.error(error?.data?.message?.[0]?.msg || "An error occurred.");
        } finally {
          await updateUI();
          setIsLoading(false);
        }
      } catch (error) {
        notify.error("Failed to delete item.");
      }
      setDeleteRecord(null);
    }
  };

  const handleBulkDelete = async (keys: any[]) => {
    if (keys) {
      try {
        try {
          setIsLoading(true);
          const response = await api.post("userService/bulkDeleteProducts", {
            signature,
            ids: keys,
          });
          notify.success(response.data.message[0].msg);
        } catch (error: any) {
          notify.error(error?.data?.message?.[0]?.msg || "An error occurred.");
        } finally {
          await updateUI();
          setIsLoading(false);
        }
      } catch (error) {
        notify.error("Failed to delete item.");
      }
    }
  };

  // 🔄 Fetch Data
  const getAllCompanies = async () => {
    try {
      const res = await api.post("userService/getAllCompanyList", {
        signature,
      });
      const companies = res?.data?.data?.data || [];
      setCompanies(companies);
    } catch (error: any) {
      notify.error(
        error?.data?.message?.[0]?.msg || "Failed to load companies.",
      );
    }
  };

  const fetchAllData = async () => {
    if (formDataCompanyChoose.company_id !== "") {
      try {
        const [getProductsRes, getCategoryRes] = await Promise.allSettled([
          api.post("userService/getProducts", {
            signature,
            company_id: formDataCompanyChoose.company_id,
            company_type: formDataCompanyChoose.company_type,
          }),
          api.post("userService/getCategory", {
            signature,
            company_id: formDataCompanyChoose.company_id,
            company_type: formDataCompanyChoose.company_type,
          }),
        ]);

        const getProducts =
          getProductsRes.status === "fulfilled"
            ? getProductsRes.value.data?.data?.data || []
            : [];
        const getCategory =
          getCategoryRes.status === "fulfilled"
            ? getCategoryRes.value.data?.data?.data || []
            : [];

        setData(getProducts);
        setFilteredData(getProducts);
        setAssetCategoryOptions(getCategory);
      } catch (error: any) {
        notify.error(error?.data?.message?.[0]?.msg || "Failed to fetch data.");
      }
    }
  };

  const updateUI = async () => {
    setIsLoading(true);
    const startTime = Date.now();

    try {
      await fetchAllData();
    } catch (error: any) {
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 300 - elapsed); // Fix negative timeout

      if (remaining > 0) {
        setTimeout(() => setIsLoading(false), remaining);
      } else {
        setIsLoading(false);
      }
    }
  };

  //Use Effects HOOK
  useEffect(() => {
    const savedView = localStorage.getItem("assetProductView") || "list";
    if (!searchParams.get("view")) {
      handleViewChange(savedView);
    }
    getAllCompanies();
  }, []);

  useEffect(() => {
    if (formDataCompanyChoose.company_id) {
      updateUI();
    }
  }, [formDataCompanyChoose]);

  useEffect(() => {
    if (viewMode) {
      localStorage.setItem("assetProductView", viewMode);
    }
  }, [viewMode]);

  useEffect(() => {
    if (companies.length) {
      const storedCompany = localStorage.getItem("selectedAssetCompany");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        handleCompanySelection({
          value: parsed.company_id,
          label: `${parsed.company_name} - ${parsed.company_type}`,
        });
      } else {
        handleCompanySelection({
          value: companies[0].id.toString(),
          label: companies[0].name + " - " + companies[0].type,
        });
      }
    }
  }, [companies]);

  // 📤 Export Handlers

  const cols = [
    "Product Name",
    "Model",
    "Brand",
    "Device ID",
    "Purchase From",
    "Purchase Date",
    "Warranty",
    "Status",
    "Created At",
  ];
  const keys = [
    "name",
    "model",
    "brand",
    "serial_no",
    "purchase_from",
    "purchase_date",
    "warranty",
    "status",
    "created_at",
  ];

  const handleExportPDF = () => {
    exportToPDF({
      data: filteredData,
      headers: cols,
      keys: keys,
      fileName: "products-data",
    });
  };

  const handleExportExcel = () => {
    exportToExcel({
      data: filteredData,
      headers: cols,
      keys: keys,
      fileName: "products-data",
    });
  };

  // Table Columns
  const columns = [
    {
      title: "Product Name",
      dataIndex: "name",
      render: (text: string, record?: any) => (
        <div className="d-flex align-items-center file-name-icon">
          <li className="avatar avatar-lg avatar-rounded object-cover flex-shrink-0">
            <ImageWithBasePath
              src={
                !record?.image_url_object?.url ||
                record?.image_url_object?.url === "null"
                  ? "assets/img/products/product-icon.png"
                  : record?.image_url_object?.url
              }
              alt="img"
            />
          </li>
          <div className="ms-2">
            <h6 className="fs-13 fw-medium mb-1 table-text-wrap">{text}</h6>
            <span className="fs-12 fw-normal table-text-wrap">
              {record.model}
            </span>
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => {
        const nameCompare = (a?.name || "").localeCompare(b?.name || "", "en", {
          sensitivity: "base",
          numeric: true,
        });

        return nameCompare;
      },
    },
    {
      title: "Brand",
      dataIndex: "brand",
      render: (text: any) => (
        <span className="badge d-inline-flex align-items-center justify-content-center badge-md badge-secondary-transparent table-text-wrap">
          {toProperCase(text)}
        </span>
      ),
      sorter: (a: any, b: any) =>
        (a?.brand || "").localeCompare(b?.brand || ""),
    },
    {
      title: "Category",
      dataIndex: "asset_category_id",
      render: (text: any, record: any) => (
        <span className="badge d-inline-flex align-items-center justify-content-center badge-md badge-info-transparent table-text-wrap">
          {record?.category?.breadcrumb}
        </span>
      ),
      sorter: (a: any, b: any) => a?.asset_category_id - b?.asset_category_id,
    },
    {
      title: "Serial No",
      dataIndex: "serial_no",
      render: (text: string) => (
        <h6 className="fs-13 fw-medium table-text-wrap">{text}</h6>
      ),
      sorter: (a: any, b: any) =>
        (a?.serial_no || "").localeCompare(b?.serial_no || ""),
    },
    {
      title: "Warranty",
      dataIndex: "warranty",
      sorter: (a: any, b: any) =>
        new Date(a.warranty).getTime() - new Date(b.warranty).getTime(),
      render: (text: string) => {
        const isExpired = new Date(text) < new Date();
        const badgeClass = isExpired
          ? "badge-soft-danger"
          : "badge-soft-success";

        return (
          <span
            className={`badge d-inline-flex align-items-center badge-sm ${badgeClass}`}
          >
            {humanReadableDate(text)}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: any) => (
        <span
          className={`badge d-inline-flex align-items-center badge-xs ${
            text === "Active"
              ? "badge-success"
              : text === "Inactive"
                ? "badge-danger"
                : "badge-warning"
          }`}
        >
          <i className="ti ti-point-filled me-1"></i>
          {text}
        </span>
      ),
      sorter: (a: any, b: any) => a?.status?.length - b?.status?.length,
    },
    {
      title: "",
      dataIndex: "actions",
      render: (text: any, record: any) => (
        <div className="action-icon d-inline-flex">
          {record.status !== "Lost" && (
            <Link
              title="Edit Product"
              to="#"
              className="me-2"
              data-bs-toggle="modal"
              data-inert={true}
              data-bs-target="#edit_asset_product_modal"
              onClick={() => handleProductEdit(record)}
            >
              <i className="ti ti-edit" />
            </Link>
          )}
          <Link
            title={record.status === "Lost" ? "Recover Product" : "Mark as Lost"}
            to="#"
            className="me-2"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#mark_product_lost_modal"
            onClick={() => setSelectedLostProduct(record)}
          >
            <i
              className={`ti ${
                record.status === "Lost"
                  ? "ti-refresh-dot text-success"
                  : "ti-alert-triangle text-warning"
              }`}
            />
          </Link>
          <Link
            title="Delete Product"
            to="#"
            data-bs-toggle="modal"
            data-inert={true}
            data-bs-target="#delete_modal"
            onClick={() => setDeleteRecord(record)}
          >
            <i className="ti ti-trash" />
          </Link>
        </div>
      ),
    },
  ];

  return (
    <>
      {isLoading && <Loader />}

      {/* Headers Section  */}
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Asset Products</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Administration</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Asset Products {isGridView ? "(Grid View)" : "(List View)"}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="me-2 mb-2">
                <div className="d-flex align-items-center border bg-white rounded p-1 me-2 icon-list">
                  <button
                    onClick={() => handleViewChange("list")}
                    className={`btn btn-icon btn-sm ${
                      !isGridView ? "active bg-primary text-white" : ""
                    } me-1`}
                    title="List View"
                  >
                    <i className="ti ti-list-tree" />
                  </button>
                  <button
                    onClick={() => handleViewChange("grid")}
                    className={`btn btn-icon btn-sm ${
                      isGridView ? "active bg-primary text-white" : ""
                    } me-1`}
                    title="Grid View"
                  >
                    <i className="ti ti-layout-grid" />
                  </button>
                </div>
              </div>
              <div className="dropdown me-2 mb-2" title="Choose Company">
                <CommonSelect
                  className="select"
                  options={companyOptions}
                  defaultValue={{
                    value: formDataCompanyChoose.company_id,
                    label:
                      truncateCompanyText(
                        formDataCompanyChoose.company_name,
                        20,
                      ) +
                      "-" +
                      formDataCompanyChoose.company_type,
                  }}
                  onChange={handleCompanySelection}
                />
              </div>

              <div className="me-2 mb-2" style={{ zIndex: 1000 }}>
                <div className="dropdown" title="Export Data">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-file-export me-1" />
                    Export
                  </Link>
                  <ul className="dropdown-menu p-3">
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        onClick={handleExportPDF}
                      >
                        <i className="ti ti-file-type-pdf me-1" />
                        Export as PDF
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        className="dropdown-item rounded-1"
                        onClick={handleExportExcel}
                      >
                        <i className="ti ti-file-type-xls me-1" />
                        Export as Excel
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mb-2 me-2" title="Add Product">
                <Link
                  to="#"
                  data-inert={true}
                  data-bs-toggle="modal"
                  data-bs-target="#add_product_modal"
                  className="btn btn-primary d-flex align-items-center"
                >
                  <i className="ti ti-circle-plus me-2" />
                  Add Product
                </Link>
              </div>
              <div className="ms-2 head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>

          {/* Product List  */}

          {isGridView ? (
            <div>
              <div className="card" style={{ zIndex: 99 }}>
                <div className="card-header">
                  <ProductGridFilter
                    data={data}
                    setFilteredData={setFilteredData}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    assetCategoryOptions={assetCategoryOptions}
                  />
                </div>
              </div>
              <div className="">
                <AssetProductGrid
                  filteredData={filteredData}
                  setDeleteRecord={setDeleteRecord}
                  handleProductEdit={handleProductEdit}
                  setSelectedLostProduct={setSelectedLostProduct}
                />
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <h5>Asset Products List</h5>
                <ProductFilter
                  data={data}
                  setFilteredData={setFilteredData}
                  assetCategoryOptions={assetCategoryOptions}
                  setSearchQuery={setSearchQuery}
                />
              </div>
              <div className="card-body p-0">
                <Table
                  dataSource={filteredData}
                  columns={columns}
                  Selection={true}
                  onBulkDelete={handleBulkDelete}
                />
              </div>
            </div>
          )}
        </div>

        {/* Product List Card Footer  */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              ZYVO
            </Link>
          </p>
        </div>
      </div>

      {/* Add Product Modal */}
      <AddProductModal
        key={`ADD-${formDataCompanyChoose.company_id}`}
        selectedCompany={formDataCompanyChoose}
        categoryHierarchy={assetCategoryOptions}
        statusOptionsList={statusOptions}
        refreshProductList={updateUI}
      />

      {/* Edit Assets Modal */}
      <EditProductModal
        key={`EDIT-${formDataCompanyChoose.company_id}`}
        statusOptionsList={statusOptions}
        refreshProductList={updateUI}
        selectedProductEdit={selectedProductEdit}
        selectedCompany={formDataCompanyChoose}
        categoryHierarchy={assetCategoryOptions}
      />

      <DeleteModal
        modalId="delete_modal"
        onDelete={handleDelete}
        title="Delete Item"
        message="Are you sure you want to delete this item? This action cannot be undone."
      />

      <MarkProductLostModal
        selectedLostProduct={selectedLostProduct}
        setSelectedLostProduct={setSelectedLostProduct}
        selectedCompany={formDataCompanyChoose}
        refreshProductList={updateUI}
      />
    </>
  );
};

export default AssetsProduct;
